import * as React from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

/**
 * Shadcn-styled confirmation dialog. Replaces every `window.confirm()` call.
 *
 * Use the `open` / `onOpenChange` pattern: the parent owns the boolean.
 * Pass `destructive` for delete/retire/cancel-charge style actions — the
 * primary button switches to the destructive variant and an alert icon
 * is shown.
 *
 * Optional `details` slot for "what does this break" context (e.g.
 * "Detaches from 4 campaigns" / "Charges $10.13 to Porkbun") — render
 * however you like.
 *
 * `loading=true` disables the buttons and shows a spinner; the caller is
 * responsible for closing the dialog after the async action resolves.
 */
export interface ConfirmDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onConfirm: () => void | Promise<void>;
    title: React.ReactNode;
    description?: React.ReactNode;
    details?: React.ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    destructive?: boolean;
    loading?: boolean;
    "data-testid"?: string;
}

export function ConfirmDialog({
    open,
    onOpenChange,
    onConfirm,
    title,
    description,
    details,
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    destructive = false,
    loading = false,
    "data-testid": testId,
}: ConfirmDialogProps): React.ReactNode {
    return (
        <Dialog open={open} onOpenChange={(o) => { if (!loading) onOpenChange(o); }}>
            <DialogContent data-testid={testId}>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        {destructive && <AlertTriangle className="h-4 w-4 text-destructive" aria-hidden="true" />}
                        {title}
                    </DialogTitle>
                    {description && <DialogDescription>{description}</DialogDescription>}
                </DialogHeader>
                {details && (
                    <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-foreground/90">
                        {details}
                    </div>
                )}
                <DialogFooter>
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={() => onOpenChange(false)}
                        disabled={loading}
                        data-testid={testId ? `${testId}-cancel` : undefined}
                    >
                        {cancelLabel}
                    </Button>
                    <Button
                        type="button"
                        variant={destructive ? "destructive" : "default"}
                        onClick={() => void onConfirm()}
                        disabled={loading}
                        data-testid={testId ? `${testId}-confirm` : undefined}
                    >
                        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                        {confirmLabel}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/**
 * Convenience hook for the common "click button → confirm → run async" flow.
 *
 *   const confirm = useConfirm();
 *   confirm.show({
 *     title: "Delete page?",
 *     description: "This can't be undone.",
 *     destructive: true,
 *     onConfirm: async () => { await deleteMut.mutateAsync(id); },
 *   });
 *   return <>{confirm.dialog}</>;
 */
export function useConfirm(): {
    show: (cfg: Omit<ConfirmDialogProps, "open" | "onOpenChange">) => void;
    dialog: React.ReactNode;
} {
    const [state, setState] = React.useState<{ open: boolean; cfg: Omit<ConfirmDialogProps, "open" | "onOpenChange"> | null; loading: boolean }>({
        open: false, cfg: null, loading: false,
    });
    const show = React.useCallback((cfg: Omit<ConfirmDialogProps, "open" | "onOpenChange">) => {
        setState({ open: true, cfg, loading: false });
    }, []);
    const dialog = state.cfg ? (
        <ConfirmDialog
            {...state.cfg}
            open={state.open}
            loading={state.loading}
            onOpenChange={(o) => setState((s) => ({ ...s, open: o }))}
            onConfirm={async () => {
                if (!state.cfg) return;
                setState((s) => ({ ...s, loading: true }));
                try {
                    await state.cfg.onConfirm();
                    setState({ open: false, cfg: null, loading: false });
                } catch (e) {
                    // Caller is expected to surface its own toast; we just clear loading.
                    setState((s) => ({ ...s, loading: false }));
                    throw e;
                }
            }}
        />
    ) : null;
    return { show, dialog };
}
